/**
 * Canvas Type Mapping — maps legacy 12-type system to simplified 5-type display system.
 *
 * Used for backward compatibility with persisted canvasState JSON that uses
 * the old type names (TASK, PERSON, SYSTEM, etc.).
 */

import type { CanvasDisplayType } from './types';

const DISPLAY_TYPE_MAP: Record<string, CanvasDisplayType> = {
  // New types (pass through)
  STEP: 'STEP',
  ACTOR: 'ACTOR',
  TOOL: 'TOOL',
  INSIGHT: 'INSIGHT',
  TRIGGER: 'TRIGGER',
  // Legacy → STEP
  TASK: 'STEP',
  PROCESS: 'STEP',
  // Legacy → ACTOR
  PERSON: 'ACTOR',
  ROLE: 'ACTOR',
  // Legacy → TOOL
  SYSTEM: 'TOOL',
  DOCUMENT: 'TOOL',
  // Legacy → INSIGHT
  RULE: 'INSIGHT',
  DECISION: 'INSIGHT',
  KPI: 'INSIGHT',
  RISK: 'INSIGHT',
  WORKAROUND: 'INSIGHT',
  BUSINESS_OBJECT: 'INSIGHT',
  // Legacy → TRIGGER
  EVENT: 'TRIGGER',
};

/** Convert any node type (new or legacy) to one of the 5 display types */
export function mapToDisplayType(type: string): CanvasDisplayType {
  return DISPLAY_TYPE_MAP[type] ?? 'STEP';
}

/** Get a human-readable sub-type label for INSIGHT and TOOL nodes */
export function getSubTypeLabel(subType: string | undefined, displayType: CanvasDisplayType): string | undefined {
  if (!subType) return undefined;

  const labels: Record<string, string> = {
    // TOOL sub-types
    SYSTEM: 'System',
    DOCUMENT: 'Document',
    // INSIGHT sub-types
    RULE: 'Rule',
    DECISION: 'Decision',
    KPI: 'KPI',
    RISK: 'Risk',
    WORKAROUND: 'Workaround',
    BUSINESS_OBJECT: 'Object',
  };

  // Only show sub-type label for types that merge multiple categories
  if (displayType === 'TOOL' || displayType === 'INSIGHT') {
    return labels[subType] ?? subType;
  }
  return undefined;
}

const EDGE_TYPE_MAP: Record<string, string> = {
  // Keep as-is
  NEXT_STEP: 'NEXT_STEP',
  USES: 'USES',
  RESPONSIBLE_FOR: 'RESPONSIBLE_FOR',
  GOVERNED_BY: 'GOVERNED_BY',
  TRIGGERS: 'TRIGGERS',
  MEASURED_BY: 'MEASURED_BY',
  MITIGATES: 'MITIGATES',
  RELATES_TO: 'RELATES_TO',
  // Legacy merges
  REFERENCES: 'USES',
  FILLS_ROLE: 'RESPONSIBLE_FOR',
  APPROVES: 'GOVERNED_BY',
  PROCESSES: 'RELATES_TO',
  WORKS_AROUND: 'MITIGATES',
};

/** Convert any edge type (new or legacy) to the simplified set */
export function mapEdgeType(type: string): string {
  return EDGE_TYPE_MAP[type] ?? 'RELATES_TO';
}

/** Derive subType from a legacy type (for backward compat when subType field is missing) */
export function deriveSubType(type: string): string | undefined {
  // Only relevant for types that map to TOOL or INSIGHT
  const needsSubType = ['SYSTEM', 'DOCUMENT', 'RULE', 'DECISION', 'KPI', 'RISK', 'WORKAROUND', 'BUSINESS_OBJECT'];
  return needsSubType.includes(type) ? type : undefined;
}
